/**
 * MetadataModal Component
 *
 * Purpose: Reusable modal component for metadata-driven models with create/edit/view modes
 * Architecture: Wraps MetadataForm in Material-UI Dialog with lifecycle management
 * Dependencies: Material-UI, MetadataForm, useMetadataModal hook
 * Author: Development Team
 * Created: 2025-01-31
 */

import React, { useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Alert,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { MetadataForm, MetadataFormHandle } from '../metadata-form/MetadataForm';
import { useMetadataModal } from './useMetadataModal';
import type { MetadataModalProps } from './types';
import { SecondaryButton } from '../common/ActionButtons';
import DialogFooter from '../common/DialogFooter';

/**
 * MetadataModal Component
 *
 * Purpose: Provides standardized modal interface for all metadata-driven models
 * Logic: Manages modal lifecycle, unsaved changes warning, form submission
 * Input: MetadataModalProps (see types.ts)
 * Output: Modal dialog with MetadataForm
 * Exceptions: Displays submission errors in alert
 */
export const MetadataModal: React.FC<MetadataModalProps> = ({
  open,
  onClose,
  title,
  infoText,
  size = 'md',
  mode = 'create',
  modelId,
  tenantId,
  pickerPermission,
  viewConfig,
  initialData,
  fixedFields,
  entityId,
  fieldOrder,
  excludeFields,
  customRenderers,
  customValidators,
  showSectionDivider,
  sectionTitle,
  columnLayout = 'double',
  onSubmit,
  onSuccess,
  onValidationError,
  submitButtonText,
  cancelButtonText = 'Cancel',
  closeButtonText = 'Close',
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  showCloseIcon = true,
  fullScreen,
  warnOnUnsavedChanges,
  loading,
  submitting: externalSubmitting,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use metadata modal hook for state management
  const {
    isSubmitting,
    submitError,
    formKey,
    isDirty: _isDirty,
    showUnsavedWarning,
    handleClose,
    handleConfirmClose,
    handleCancelClose,
    handleFormChange,
    handleSubmit,
  } = useMetadataModal({
    open,
    onClose,
    mode,
    onSubmit,
    onSuccess,
    warnOnUnsavedChanges,
  } as MetadataModalProps);

  const formRef = useRef<MetadataFormHandle>(null);

  // Determine if modal should be full screen
  const isFullScreen = fullScreen ?? (isMobile && (size === 'lg' || size === 'xl'));

  // Read-only mode check
  const isReadOnly = mode === 'view';

  // Determine button text
  const defaultSubmitText = mode === 'create' ? 'Create' : 'Save';
  const submitText = submitButtonText || defaultSubmitText;

  /**
   * Handle backdrop click
   *
   * Purpose: Close modal when backdrop is clicked
   * Logic: Respects disableBackdropClick prop and submission state
   */
  const handleBackdropClick = useCallback(
    (_event: any, reason: string) => {
      if (disableBackdropClick && reason === 'backdropClick') return;
      if (isSubmitting || externalSubmitting) return;
      handleClose();
    },
    [disableBackdropClick, isSubmitting, externalSubmitting, handleClose]
  );

  /**
   * Handle keyboard events
   *
   * Purpose: Handle Escape key press
   * Logic: Prevents default behavior if disableEscapeKeyDown is true
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disableEscapeKeyDown && event.key === 'Escape') {
        event.stopPropagation();
      }
    },
    [disableEscapeKeyDown]
  );

  return (
    <>
      {/* Main Modal */}
      <Dialog
        open={open}
        onClose={handleBackdropClick}
        maxWidth={size}
        fullWidth
        fullScreen={isFullScreen}
        onKeyDown={handleKeyDown}
        aria-labelledby="metadata-modal-title"
        aria-describedby={infoText ? 'metadata-modal-description' : undefined}
        disableEscapeKeyDown={disableEscapeKeyDown || isSubmitting || Boolean(externalSubmitting)}
      >
        {/* Title Bar */}
        <DialogTitle id="metadata-modal-title">
          {title ||
            `${mode === 'create' ? 'Create' : mode === 'edit' ? 'Edit' : 'View'} ${modelId}`}
          {showCloseIcon && (
            <IconButton
              aria-label="close modal"
              onClick={() => handleClose()}
              disabled={isSubmitting || Boolean(externalSubmitting)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        {/* Content */}
        <DialogContent dividers>
          {/* Info Box */}
          {infoText && (
            <Alert
              severity="info"
              icon={<InfoIcon />}
              sx={{ mb: 3 }}
              id="metadata-modal-description"
            >
              {infoText}
            </Alert>
          )}

          {/* Submission Error */}
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Form */}
          {!loading && (
            <MetadataForm
              ref={formRef}
              key={formKey}
              modelId={modelId}
              tenantId={tenantId}
              mode={mode === 'view' ? 'edit' : mode}
              viewConfig={viewConfig}
              initialData={initialData}
              fixedFields={fixedFields}
              entityId={entityId}
              fieldOrder={fieldOrder}
              excludeFields={excludeFields}
              customRenderers={customRenderers}
              customValidators={customValidators}
              showSectionDivider={showSectionDivider}
              sectionTitle={sectionTitle}
              columnLayout={columnLayout}
              onSubmit={handleSubmit}
              onValidationError={onValidationError}
              onChange={handleFormChange}
              loading={Boolean(externalSubmitting) || isSubmitting}
              readOnly={isReadOnly}
              pickerPermission={pickerPermission}
            />
          )}
        </DialogContent>

        {/* Actions */}
        {!loading && (
          <DialogFooter>
            {isReadOnly ? (
              <SecondaryButton
                onClick={() => handleClose()}
                disabled={isSubmitting || Boolean(externalSubmitting)}
              >
                {closeButtonText}
              </SecondaryButton>
            ) : (
              <>
                <SecondaryButton
                  onClick={() => handleClose()}
                  disabled={isSubmitting || Boolean(externalSubmitting)}
                >
                  {cancelButtonText}
                </SecondaryButton>
                <Button
                  variant="contained"
                  disabled={isSubmitting || Boolean(externalSubmitting)}
                  onClick={() => {
                    formRef.current?.submit();
                  }}
                >
                  {isSubmitting || externalSubmitting ? <CircularProgress size={24} /> : submitText}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </Dialog>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog
        open={showUnsavedWarning}
        onClose={handleCancelClose}
        aria-labelledby="unsaved-warning-title"
        aria-describedby="unsaved-warning-description"
      >
        <DialogTitle id="unsaved-warning-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <Box id="unsaved-warning-description">
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </Box>
        </DialogContent>
        <DialogFooter>
          <SecondaryButton onClick={handleCancelClose} autoFocus>
            Stay
          </SecondaryButton>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Leave
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};
